import { useRef, useState } from "react";
import { FiX, FiUploadCloud, FiFileText, FiTrash2 } from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";
import { message } from "antd";
import dayjs from "dayjs";
import LabOrderService from "../../../../Services/LabOrderService";

export default function UploadResultModal({
  isOpen,
  onClose,
  onSuccess,
  selectedItem,
}) {
  const loginObj =
    sessionStorage.getItem("login_result") ||
    localStorage.getItem("login_result");
  const userData = loginObj ? JSON.parse(loginObj) : null;
  const currentUserId = userData?.user?.userId || "DZU5891001";

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [base64Data, setBase64Data] = useState("");
  const [fileType, setFileType] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const orderId =
    selectedItem?.rawHeader?.labTestOrderId ||
    selectedItem?.orderId ||
    selectedItem?.labTestOrderId ||
    "-";
  const reportId =
    selectedItem?.hdrId ||
    selectedItem?.rawHeader?.id ||
    selectedItem?.id;

  const processFile = (file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      message.error("File size must be under 10MB");
      return;
    }

    let ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (ext === "jpg") ext = "jpeg";
    const detectedType = ext || file.type.split("/")[1] || "jpeg";

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result;
      const base64 = typeof result === "string" ? result.split(",")[1] : "";
      setPreviewUrl(result);
      setBase64Data(base64);
      setFileType(detectedType);
      setSelectedFile(file);
    };
    reader.onerror = () => {
      message.error("Failed to read the file");
    };
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setBase64Data("");
    setFileType("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveResultDocument = async () => {
    if (!selectedItem) {
      message.error("No record selected");
      return;
    }
    if (!base64Data) {
      message.error("Please choose a file to upload");
      return;
    }

    const nowStr = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const payload = {
      modifiedBy: currentUserId,
      modifiedDate: nowStr,
      id: reportId,
      labTestOrderId: orderId,
      resultDocument: base64Data,
      resultDocumentType: fileType || "jpeg",
      status: 1,
    };

    console.log("Saving updateResultDocument payload:", payload);

    setIsUploading(true);
    try {
      await LabOrderService.updateResultDocument(payload);
      message.success("Result document uploaded successfully");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error updating result document:", err);
      message.error("Failed to upload result document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white rounded-[18px] overflow-hidden shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#edf1f7]">
          <div>
            <h3 className="text-[16px] font-bold text-[#111827]">
              Upload Result Document
            </h3>
            <p className="text-[12px] text-[#6b7280]">
              {selectedItem?.testName || "Rad/Path Order"} &bull;{" "}
              <span className="font-mono text-[#008f9f]">{orderId}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#e5e7eb] hover:bg-gray-100 text-[#6b7280] transition-colors"
          >
            <FiX className="text-[15px]" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*,application/pdf"
            className="hidden"
          />

          {/* Upload Dropzone (when no file selected) */}
          {!selectedFile ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-[#008f9f] bg-[#f0fdfd]"
                  : "border-[#cbd5e1] hover:border-[#008f9f] hover:bg-[#fafafa]"
              }`}
            >
              <div className="w-12 h-12 rounded-full bg-[#f0fdfd] text-[#008f9f] flex items-center justify-center mb-3">
                <FiUploadCloud size={24} />
              </div>
              <p className="text-[13px] font-semibold text-[#1e293b]">
                Click to choose file or drag and drop
              </p>
              <p className="text-[11px] text-[#64748b] mt-1">
                Supports JPEG, PNG, WebP, or PDF (up to 10MB)
              </p>
            </div>
          ) : (
            /* File Selected & Preview */
            <div className="space-y-3">
              {/* File Info Card */}
              <div className="flex items-center justify-between p-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#f0fdfd] text-[#008f9f] flex items-center justify-center shrink-0">
                    <FiFileText size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-[#1e293b] truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-[11px] text-[#64748b]">
                      {(selectedFile.size / 1024).toFixed(1)} KB &bull;{" "}
                      <span className="uppercase font-semibold text-[#008f9f]">
                        {fileType}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[12px] text-[#008f9f] hover:underline px-2 py-1"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveSelectedFile}
                    className="w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
                    title="Remove file"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Preview Box */}
              <div className="border border-[#e2e8f0] rounded-xl p-3 bg-[#fafafa]">
                <p className="text-[11px] font-semibold text-[#64748b] mb-2">
                  Document Preview
                </p>
                {fileType === "pdf" ? (
                  <div className="w-full h-64 rounded-lg overflow-hidden border border-[#e2e8f0] bg-white flex flex-col items-center justify-center">
                    <iframe
                      src={previewUrl}
                      className="w-full h-full"
                      title="PDF Preview"
                    />
                  </div>
                ) : (
                  <div className="w-full max-h-72 rounded-lg overflow-hidden border border-[#e2e8f0] bg-white flex items-center justify-center p-2">
                    <img
                      src={previewUrl}
                      alt="Document Preview"
                      className="max-h-64 max-w-full object-contain rounded"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-[#edf1f7] flex justify-end gap-3 bg-[#fafbff]">
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="px-5 py-2 bg-white border border-[#e5e7eb] rounded-lg text-[13px] font-semibold text-[#374151] hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveResultDocument}
            disabled={isUploading || !base64Data}
            className="px-5 py-2 rounded-lg bg-[#008f9f] text-white text-[13px] font-semibold hover:bg-[#007a8a] transition-colors flex items-center gap-2 disabled:opacity-50 shadow-sm"
          >
            {isUploading && <FaSpinner className="animate-spin text-[13px]" />}
            {isUploading ? "Uploading\u2026" : "Save Document"}
          </button>
        </div>
      </div>
    </div>
  );
}
